import { useMemo } from "react"
import {
	IconCheckOutline18 as Check,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"

import type { SharedFilterItemsProps } from "@/components/filters"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"
import { Owner } from "@/screens/app/threads/components/right-bar/customer-properties/icons/owner"
import { useCompaniesFilterState } from "../companies-filter-state"

export function OwnerFilterItems({ onSelect }: SharedFilterItemsProps) {
	const { ownerIds, addOwner, removeOwner } = useCompaniesFilterState()
	const workspace = useZeroWorkspace()

	const teamId = workspace?.defaultLinearTeamId ?? ""
	const membersQuery = api.linear.members.useQuery(
		{ teamId },
		{ enabled: !!teamId, cacheTime: Infinity, staleTime: Infinity },
	)

	const members = useMemo(
		() =>
			[...(membersQuery.data ?? [])].sort((a, b) =>
				a.displayName.localeCompare(b.displayName),
			),
		[membersQuery.data],
	)

	return (
		<>
			<CommandItem
				key="no-owner"
				onSelect={() => {
					if (ownerIds.includes(null)) {
						removeOwner(null)
					} else {
						addOwner(null)
					}
					onSelect()
				}}
			>
				<div className="flex w-full items-center gap-2">
					<UserX className="h-4 w-4 shrink-0" />
					<span className="flex-1 truncate">No owner</span>
					<Check
						className={cn("h-4 w-4 shrink-0", {
							"opacity-100": ownerIds.includes(null),
							"opacity-0": !ownerIds.includes(null),
						})}
					/>
				</div>
			</CommandItem>
			<hr className="my-2" />
			{members.map((member) => {
				const isSelected = ownerIds.includes(member.id)
				return (
					<CommandItem
						key={member.id}
						onSelect={() => {
							if (isSelected) {
								removeOwner(member.id)
							} else {
								addOwner(member.id)
							}
							onSelect()
						}}
					>
						<div className="flex w-full items-center gap-2">
							{member.avatarUrl ? (
								<img
									src={member.avatarUrl}
									alt={member.displayName}
									className="h-4 w-4 shrink-0 rounded-full object-cover"
								/>
							) : (
								<Owner className="h-4 w-4 shrink-0 text-secondary" />
							)}
							<span className="flex-1 truncate">{member.displayName}</span>
							<Check
								className={cn("h-4 w-4 shrink-0", {
									"opacity-100": isSelected,
									"opacity-0": !isSelected,
								})}
							/>
						</div>
					</CommandItem>
				)
			})}
		</>
	)
}
